import { useEffect, useRef, useState } from 'react';
import { Blocks, Package, Upload, CheckCircle, AlertCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { api, apiUpload, formatDate } from '../../lib/api';

// Self-paced content source: a SCORM package (existing courses) or a Course
// Engine package (course.json + media). Imports are immutable versions; the
// newest becomes current, and learners stay on the version they started.
export default function EngineContentCard({ courseId, isNew, form, updateField, course, setCourse }) {
  const isEngine = form.content_format === 'engine';
  const [versions, setVersions] = useState(null);
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const load = () =>
    api(`/api/admin/course-engine/courses/${courseId}/versions`)
      .then((d) => setVersions(d.versions))
      .catch(() => setVersions([]));

  useEffect(() => {
    if (!isNew && isEngine) load();
  }, [courseId, isNew, isEngine]); // eslint-disable-line react-hooks/exhaustive-deps

  const importPackage = async () => {
    if (!file) return;
    setResult(null);
    setProgress(0);
    const fd = new FormData();
    fd.append('package', file);
    try {
      const r = await apiUpload(`/api/admin/course-engine/courses/${courseId}/versions`, fd, { onProgress: setProgress });
      setResult({ ok: true, unchanged: !!r.unchanged, version: r.version, warnings: r.warnings || [] });
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
      if (!r.unchanged) setCourse((c) => ({ ...c, current_engine_version_id: r.version.id }));
      load();
    } catch (err) {
      setResult({ ok: false, error: err.body?.detail || err.body?.error || err.message, issues: err.body?.issues || [], warnings: err.body?.warnings || [] });
    } finally {
      setProgress(null);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5 mb-5">
      <h3 className="font-semibold mb-4 flex items-center gap-2">
        <Blocks size={16} className="text-slate-400" />
        Course content
      </h3>

      <fieldset>
        <legend className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2">Content format</legend>
        <div className="grid sm:grid-cols-2 gap-3">
          {[
            { value: 'scorm', icon: Package, label: 'SCORM package', sub: 'Rise or Storyline .zip, played in a frame' },
            { value: 'engine', icon: Blocks, label: 'Course Engine', sub: 'Native lessons built from blocks' },
          ].map((o) => {
            const active = (form.content_format || 'scorm') === o.value;
            const Icon = o.icon;
            return (
              <label
                key={o.value}
                className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${active ? 'border-brand bg-brand/5' : 'border-slate-200 hover:border-slate-300'}`}
              >
                <input type="radio" name="content_format" value={o.value} checked={active} onChange={() => updateField('content_format', o.value)} className="sr-only" />
                <span className={`w-10 h-10 rounded-lg flex items-center justify-center ${active ? 'bg-brand text-white' : 'bg-slate-100 text-slate-500'}`}>
                  <Icon size={20} />
                </span>
                <span>
                  <span className={`block font-semibold ${active ? 'text-slate-900' : 'text-slate-700'}`}>{o.label}</span>
                  <span className="block text-xs text-slate-500">{o.sub}</span>
                </span>
              </label>
            );
          })}
        </div>
        {course && (form.content_format || 'scorm') !== (course.content_format || 'scorm') && (
          <p className="mt-2 text-xs text-amber-700">Save the course to switch the format.</p>
        )}
      </fieldset>

      {isEngine && isNew && <p className="mt-4 text-sm text-slate-500">Save the course first, then import a course package.</p>}

      {isEngine && !isNew && (
        <div className="mt-5 space-y-4">
          <div>
            <label htmlFor="engine-package" className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">
              Import a course package
            </label>
            <p className="text-xs text-slate-500 mb-2">
              A .zip with <code>course.json</code> at its root and the media it uses, or a bare <code>course.json</code> with no media. Each import is a new version.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <input
                id="engine-package"
                ref={inputRef}
                type="file"
                accept=".zip,.json,application/zip,application/json"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="text-sm file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-slate-200"
              />
              <button
                type="button"
                onClick={importPackage}
                disabled={!file || progress !== null}
                className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40"
              >
                {progress !== null ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {progress !== null ? `Importing… ${progress}%` : 'Import'}
              </button>
            </div>
          </div>

          {result && <ImportResult result={result} />}

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Versions</p>
            {versions === null ? (
              <p className="text-sm text-slate-500">Loading…</p>
            ) : versions.length === 0 ? (
              <p className="text-sm text-slate-500">No content imported yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                {versions.map((v) => (
                  <li key={v.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 text-sm">
                    <span className="font-semibold">v{v.version}</span>
                    <span className="text-slate-700">{v.title}</span>
                    <span className="text-slate-500">
                      {v.lesson_count} lesson{v.lesson_count === 1 ? '' : 's'}, {v.block_count} blocks
                    </span>
                    <span className="text-slate-500">{formatDate(v.created_at)}</span>
                    <span className="ml-auto flex items-center gap-2">
                      {v.publish_ready ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle size={13} /> Ready to publish</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle size={13} /> Draft only</span>
                      )}
                      {v.is_current && <span className="rounded-full bg-slate-900 px-2 py-0.5 text-xs font-semibold text-white">Current</span>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-2 text-xs text-slate-500">
              “Draft only” versions use blocks that aren’t marked ready yet. They can be tested on a draft course with a comp enrollment, but the course can’t be published until the current version is ready.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function IssueList({ items, tone }) {
  if (!items.length) return null;
  const shown = items.slice(0, 12);
  return (
    <ul className={`mt-2 space-y-1 text-xs ${tone === 'error' ? 'text-rose-800' : 'text-amber-800'}`}>
      {shown.map((i, n) => (
        <li key={n}>
          {i.message}
          {i.path?.length ? <code className="ml-1 text-slate-500">{i.path.join('.')}</code> : null}
        </li>
      ))}
      {items.length > shown.length && <li>…and {items.length - shown.length} more</li>}
    </ul>
  );
}

function ImportResult({ result }) {
  if (!result.ok) {
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50 p-3" role="alert">
        <p className="flex items-center gap-1.5 text-sm font-semibold text-rose-800">
          <AlertCircle size={14} /> Import refused: {result.error === 'invalid_course' ? 'the course file has errors' : result.error}
        </p>
        <IssueList items={result.issues} tone="error" />
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3" role="status">
      <p className="flex items-center gap-1.5 text-sm font-semibold text-emerald-800">
        <CheckCircle size={14} />
        {result.unchanged ? `No changes; v${result.version.version} is still current.` : `Imported v${result.version.version}. It’s now the current version.`}
      </p>
      {result.warnings.length > 0 && (
        <details className="mt-1">
          <summary className="cursor-pointer text-xs text-amber-800">{result.warnings.length} warning{result.warnings.length === 1 ? '' : 's'}</summary>
          <IssueList items={result.warnings} tone="warning" />
        </details>
      )}
    </div>
  );
}
